import { z } from "zod";
import { moment } from "../../../date";
import { ZodDateString } from "../../zod-types/zod-date-string";
import { getTicketParamsSchema } from "./get-ticket.schema";

export const patchTicketDatesParamsSchema = getTicketParamsSchema;

export const patchTicketDatesSchema = z
	.object({
		dateMode: z.enum(["date", "week"]).nullish(),
		/** Both ends are mandatory: `tickets.start_date` and `tickets.end_date` are NOT NULL. */
		startDate: ZodDateString,
		endDate: ZodDateString,
		/**
		 * Where the estimated work begins inside the window. Absent leaves whatever is stored, which
		 * is what a caller changing only the window sends; null clears the placement back to "starts
		 * at the window's own start".
		 */
		workStartDate: ZodDateString.nullish(),
		/**
		 * How many working days the work takes. Absent leaves whatever is stored, which is what a
		 * caller moving the bar sends; the column is NOT NULL, so there is no clearing it.
		 *
		 * Its ceiling is **not** enforced here. A working day is whatever the ticket's project says
		 * it is, and a body carries no project — the handler reads one and clamps there, which is
		 * also the only place a caller cannot widen the site's week by claiming it.
		 */
		estimatedDays: z.coerce.number().int().min(1).optional(),
	})
	.transform((value) => {
		// Dates entered the wrong way round are swapped rather than refused: the two inputs are a
		// span, and which one the user filled first is not information worth an error. A one-day
		// ticket keeps both ends on the same day.
		if (moment(value.startDate).isAfter(value.endDate)) {
			const _startDate = value.startDate;
			value.startDate = value.endDate;
			value.endDate = _startDate;
		}
		// A placement outside the window it is sent with is pulled back to the nearest edge rather
		// than refused. The column carries a check constraint saying the same thing, so an
		// unclamped value would reach the database as an error the caller cannot act on.
		//
		// The window is the one the board draws: a 'week' ticket's dates name calendar weeks, so its
		// work may sit anywhere from the first week's Monday to the last week's Sunday. Clamping to
		// the two stored days instead would refuse most of the bar the reader can see and drag.
		const isWeekMode = value.dateMode === "week";
		const from = isWeekMode ? moment(value.startDate).startOf("week").db() : value.startDate;
		const to = isWeekMode ? moment(value.endDate).endOf("week").db() : value.endDate;

		if (value.workStartDate) {
			if (value.workStartDate < from) value.workStartDate = from;
			if (value.workStartDate > to) value.workStartDate = to;
		}

		return value;
	});

export type PatchTicketDatesSchema = z.infer<typeof patchTicketDatesSchema>;
